import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import {
  type ConfigItem,
  listarConfiguracao,
  listarRecompensas,
  type Recompensa,
  salvarConfiguracao,
  salvarRecompensa,
} from '../api/campanha';
import { useToast } from '../components/toast';
import { CabecalhoTela } from '../components/Shell';
import { AreaTexto, BlocoAviso, Botao, Campo, Chip, LinhasEsqueleto } from '../components/ui';
import { mensagemDeErro, MSG_ERRO_REDE } from '../lib/erros';
import { ApiError } from '../lib/http';

export function CampanhaPagina(): ReactNode {
  return (
    <div className="flex flex-col gap-8">
      <CabecalhoTela titulo="Campanha" />
      <SecaoRecompensas />
      <SecaoConfiguracao />
    </div>
  );
}

// ── Recompensas ──────────────────────────────────────────────────────────────
function SecaoRecompensas(): ReactNode {
  const q = useQuery({ queryKey: ['recompensas'], queryFn: listarRecompensas });

  return (
    <section>
      <h2 className="mb-2 font-display text-bloco-titulo font-bold uppercase text-tinta-forte">
        Brindes do indicador
      </h2>
      <BlocoAviso tom="aviso">
        Desativar um brinde não muda o ciclo (ele é sempre de 4 níveis). Serve só para sinalizar "em
        falta". O texto vigente é congelado no voucher no momento da concessão — mudar aqui não
        altera vouchers já concedidos.
      </BlocoAviso>
      <div className="mt-3">
        {q.isLoading && <LinhasEsqueleto linhas={4} colunas={4} />}
        {q.isError && (
          <BlocoAviso tom="aviso" aoTentarDeNovo={() => void q.refetch()}>
            Não foi possível carregar os brindes.
          </BlocoAviso>
        )}
        {q.data && (
          <table className="w-full border-collapse text-corpo">
            <thead>
              <tr className="border-b border-linha text-left font-display text-rotulo uppercase text-tinta-2">
                <th className="py-2 font-normal">Nível</th>
                <th className="py-2 font-normal">Descrição</th>
                <th className="py-2 font-normal">Ativo</th>
                <th className="py-2 font-normal">Custo estimado</th>
                <th className="py-2 font-normal" />
              </tr>
            </thead>
            <tbody>
              {q.data.map((r) => (
                <LinhaRecompensa key={r.nivel} r={r} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function LinhaRecompensa({ r }: { r: Recompensa }): ReactNode {
  const qc = useQueryClient();
  const toast = useToast();
  const [descricao, setDescricao] = useState(r.descricao);
  const [ativo, setAtivo] = useState(r.ativo);
  const [custo, setCusto] = useState(r.custoEstimado == null ? '' : String(r.custoEstimado));
  const [erro, setErro] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      salvarRecompensa(r.nivel, {
        descricao: descricao.trim(),
        ativo,
        custoEstimado: custo.trim() === '' ? null : Number(custo.replace(',', '.')),
      }),
    onSuccess: () => {
      toast(`Brinde do nível ${r.nivel} atualizado.`);
      void qc.invalidateQueries({ queryKey: ['recompensas'] });
      setErro(null);
    },
    onError: (e) =>
      setErro(e instanceof ApiError && !e.ehRede ? mensagemDeErro(e.codigo) : MSG_ERRO_REDE),
  });

  const alterado =
    descricao !== r.descricao ||
    ativo !== r.ativo ||
    (custo.trim() === ''
      ? r.custoEstimado != null
      : Number(custo.replace(',', '.')) !== r.custoEstimado);

  return (
    <tr className="border-b border-linha align-top">
      <td className="py-2.5 text-tinta-forte">{r.nivel}</td>
      <td className="py-2.5">
        <input
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
          className="w-full rounded-1 border border-linha-forte bg-superficie-alta px-2 py-1 text-corpo text-tinta-forte"
        />
      </td>
      <td className="py-2.5">
        <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
      </td>
      <td className="py-2.5">
        <input
          value={custo}
          inputMode="decimal"
          placeholder="—"
          onChange={(e) => setCusto(e.target.value)}
          className="w-24 rounded-1 border border-linha-forte bg-superficie-alta px-2 py-1 text-corpo text-tinta-forte"
        />
      </td>
      <td className="py-2.5 text-right">
        <Botao
          variante="secundario"
          className="h-8 px-3 text-[0.8125rem]"
          disabled={!alterado}
          carregando={mut.isPending}
          onClick={() => mut.mutate()}
        >
          Salvar
        </Botao>
        {erro && <p className="mt-1 text-nota text-erro">{erro}</p>}
      </td>
    </tr>
  );
}

// ── Configuração ─────────────────────────────────────────────────────────────
const GRUPOS: { titulo: string; chaves: string[] }[] = [
  { titulo: 'Campanha', chaves: ['campanha_inicio', 'campanha_fim', 'validade_codigo_dias'] },
  { titulo: 'Brinde do indicado', chaves: ['brinde_indicado_descricao'] },
  { titulo: 'Termos', chaves: ['termos_campanha_texto'] },
  {
    titulo: 'Loja',
    chaves: [
      'loja_bairro',
      'loja_endereco',
      'loja_horario',
      'loja_whatsapp',
      'loja_descricao_curta',
    ],
  },
];

function SecaoConfiguracao(): ReactNode {
  const q = useQuery({ queryKey: ['configuracao'], queryFn: listarConfiguracao });

  return (
    <section>
      <h2 className="mb-2 font-display text-bloco-titulo font-bold uppercase text-tinta-forte">
        Configuração
      </h2>
      {q.isLoading && <LinhasEsqueleto linhas={6} colunas={2} />}
      {q.isError && (
        <BlocoAviso tom="aviso" aoTentarDeNovo={() => void q.refetch()}>
          Não foi possível carregar a configuração.
        </BlocoAviso>
      )}
      {q.data && (
        <div className="flex flex-col gap-5">
          {GRUPOS.map((g) => {
            const itens = q.data.filter((i) => g.chaves.includes(i.chave));
            if (itens.length === 0) return null;
            return (
              <div key={g.titulo}>
                <h3 className="mb-1 font-display text-rotulo uppercase text-tinta-2">{g.titulo}</h3>
                <div className="flex flex-col gap-3">
                  {itens.map((i) => (
                    <EditorChave key={i.chave} item={i} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function EditorChave({ item }: { item: ConfigItem }): ReactNode {
  const qc = useQueryClient();
  const toast = useToast();
  const [valor, setValor] = useState(item.valor);
  const [erro, setErro] = useState<string | null>(null);
  const grande = item.chave === 'termos_campanha_texto';

  const mut = useMutation({
    mutationFn: () => salvarConfiguracao(item.chave, valor),
    onSuccess: () => {
      toast(`«${item.chave}» atualizada.`);
      void qc.invalidateQueries({ queryKey: ['configuracao'] });
      setErro(null);
    },
    onError: (e) =>
      setErro(
        e instanceof ApiError && e.codigo === 'validacao'
          ? `Valor fora do formato esperado para «${item.chave}».`
          : e instanceof ApiError && !e.ehRede
            ? mensagemDeErro(e.codigo)
            : MSG_ERRO_REDE,
      ),
  });

  return (
    <div className="rounded-2 border border-linha bg-superficie p-3">
      <div className="mb-1 flex items-center gap-2">
        <span className="font-codigo text-[0.8125rem] text-tinta-forte">{item.chave}</span>
        {item.publica && <Chip>Pública</Chip>}
      </div>
      {item.chave === 'campanha_fim' && (
        <p className="mb-2 text-nota text-tinta-2">
          Mudar esta data muda quando as indicações param de gerar brinde (regra 8). O dia informado
          conta inteiro como vigente.
        </p>
      )}
      {grande ? (
        <AreaTexto
          rotulo="Valor"
          value={valor}
          onChange={(e) => setValor(e.target.value)}
          className="min-h-32"
        />
      ) : (
        <Campo rotulo="Valor" value={valor} onChange={(e) => setValor(e.target.value)} />
      )}
      {erro && <p className="mt-1 text-nota text-erro">{erro}</p>}
      <div className="mt-2">
        <Botao
          variante="secundario"
          className="h-8 px-3 text-[0.8125rem]"
          disabled={valor === item.valor}
          carregando={mut.isPending}
          onClick={() => mut.mutate()}
        >
          Salvar
        </Botao>
      </div>
    </div>
  );
}
